import { memo, Suspense, useEffect } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

import { initAppData } from '@/features/app-data';
import { onboardingReducer } from '@/features/onboarding';

import { useSessionIsAuthorized } from '@/entities/auth';
import {
    useViewerData,
    useViewerHasDoneOnboarding,
    viewerReducer,
} from '@/entities/viewer';

import { getMainPath } from '@/shared/constants/router';
import {
    type ReducersList,
    useAppDispatch,
    useDynamicReducersLoader,
} from '@/shared/lib/store';
import { PageLoader } from '@/shared/ui/page-loader';

import cls from './onboarding-layout.module.css';

const reducers: ReducersList = {
    onboarding: onboardingReducer,
    viewer: viewerReducer,
};

export const OnboardingLayout = memo(() => {
    const dispatch = useAppDispatch();
    const isAuthorized = useSessionIsAuthorized();
    const viewerData = useViewerData();
    const hasCompletedOnboarding = useViewerHasDoneOnboarding();

    useDynamicReducersLoader({ reducers, needRemoveAfterUnmount: false });

    useEffect(() => {
        if (isAuthorized) {
            dispatch(initAppData());
        }
    }, [isAuthorized, dispatch]);

    if (!viewerData) {
        return <PageLoader />;
    }

    // Redirect to main page if onboarding already completed
    if (hasCompletedOnboarding) {
        return (
            <Navigate
                replace
                to={getMainPath()}
            />
        );
    }

    return (
        <div className={cls.layout}>
            <Suspense
                fallback={
                    <div className={cls.loaderContainer}>
                        <PageLoader />
                    </div>
                }
            >
                <Outlet />
            </Suspense>
        </div>
    );
});

OnboardingLayout.displayName = 'OnboardingLayout';
